import { useCallback } from 'react';
import type { QuickFillItem } from '../../../shared/types';
import {
  copyToClipboard,
  parseFromTSV,
  readFromClipboard,
  serializeToTSV,
} from '../../../shared/lib/utils/clipboardUtils';
import type { QuickFillSelectedCell } from '../lib/quickFillNavigationModel';
import { buildQuickFillCellPastePlan } from '../lib/quickFillPageModel';
import type { QuickFillMutationOperations } from './useQuickFillMutationOperations';

export interface QuickFillClearTarget {
  rowIndices: number[];
  columnLabels: string[];
  scope?: 'selection' | 'column';
}

// Declared input contract: only these fields of the mutation facade are used.
export type QuickFillClipboardContext = Pick<QuickFillMutationOperations,
  | 'selectedRows'
  | 'quickFillData'
  | 'properties'
  | 'snackbar'
  | 'canEditCell'
  | 'applyFieldValueToRows'
  | 'captureSessionUndoBeforeMutation'
  | 'setQuickFillData'
>;

export function useQuickFillClipboardOperations(context: QuickFillClipboardContext) {
  const {
    selectedRows, quickFillData, properties, snackbar, canEditCell,
    applyFieldValueToRows, captureSessionUndoBeforeMutation, setQuickFillData,
  } = context;

  const handleCopySelected = useCallback(async (cells?: QuickFillSelectedCell[]) => {
    const data = Array.isArray(quickFillData) ? quickFillData : [];
    const visibleColumns = ['Name', ...(properties || [])
      .filter((property) => property?.checked && property.label !== 'Name')
      .map((property) => property.label)];
    const validCells = (cells || []).filter((cell) =>
      Number.isInteger(cell.rowIndex) && cell.rowIndex >= 0 && cell.rowIndex < data.length &&
      typeof cell.columnLabel === 'string' && cell.columnLabel.trim() !== '');
    if (cells) {
      if (validCells.length === 0) return;
      try {
        const selectedColumnSet = new Set(validCells.map((cell) => cell.columnLabel as string));
        const columns = [
          'Name',
          ...visibleColumns.filter(
            (label) => label !== 'Name' && selectedColumnSet.has(label)
          ),
        ];
        selectedColumnSet.forEach((label) => {
          if (label !== 'Name' && !columns.includes(label)) columns.push(label);
        });
        const rowIndices = Array.from(new Set(validCells.map((cell) => cell.rowIndex)))
          .sort((left, right) => left - right);
        const selectedKeys = new Set(validCells.map(
          (cell) => `${cell.rowIndex}|${cell.columnLabel}`
        ));
        const rows = rowIndices.map((rowIndex) => columns.map((label) => (
          label === 'Name'
            ? String(data[rowIndex]?.Name ?? '')
            : selectedKeys.has(`${rowIndex}|${label}`)
            ? String(data[rowIndex]?.[label as keyof QuickFillItem] ?? '')
            : ''
        )).join('\t'));
        await copyToClipboard([columns.join('\t'), ...rows].join('\n'));
        snackbar.showSuccess(
          `Copied ${validCells.length} cell${validCells.length === 1 ? '' : 's'} to clipboard`
        );
      } catch {
        snackbar.showError('Failed to copy to clipboard');
      }
      return;
    }
    if (selectedRows.length === 0) return;
    try {
      const selectedItems = selectedRows.map((index) => data[index])
        .filter((item): item is QuickFillItem => Boolean(item));
      if (selectedItems.length === 0) return;
      await copyToClipboard(serializeToTSV(selectedItems, visibleColumns));
      snackbar.showSuccess(`Copied ${selectedItems.length} rows to clipboard`);
    } catch {
      snackbar.showError('Failed to copy to clipboard');
    }
  }, [properties, quickFillData, selectedRows, snackbar]);

  const handlePaste = useCallback(async (targetCells?: QuickFillSelectedCell[]) => {
    try {
      const parsedItems = parseFromTSV(await readFromClipboard());
      if (parsedItems.length === 0) {
        snackbar.showWarning('Clipboard does not contain tabular data to paste');
        return;
      }
      const data = Array.isArray(quickFillData) ? quickFillData : [];
      const cellPastePlan = targetCells
        ? buildQuickFillCellPastePlan(parsedItems, targetCells, data.length)
        : null;
      if (cellPastePlan) {
        let updatedData = [...data];
        const touchedRows = new Set<number>();
        let appliedValues = 0;
        let blockedCells = 0;
        cellPastePlan.updates.forEach((update) => {
          const row = updatedData[update.rowIndex];
          if (!canEditCell(row, update.field)) {
            blockedCells++;
            return;
          }
          const applied = applyFieldValueToRows(
            updatedData, [update.rowIndex], update.field, update.value
          );
          updatedData = applied.updatedData;
          applied.touchedRowIndices.forEach((rowIndex) => touchedRows.add(rowIndex));
          if (applied.touchedRowIndices.length > 0) appliedValues++;
        });
        if (touchedRows.size === 0) {
          snackbar.showWarning('No matching editable cells were updated');
        } else {
          captureSessionUndoBeforeMutation('Paste');
          setQuickFillData(updatedData);
          snackbar.showSuccess(
            `Pasted ${appliedValues} value${appliedValues === 1 ? '' : 's'} across ${touchedRows.size} row${touchedRows.size === 1 ? '' : 's'}`
          );
        }
        if (blockedCells > 0) {
          snackbar.showWarning(`${blockedCells} cell${blockedCells === 1 ? '' : 's'} skipped: read-only or unavailable`);
        }
        return;
      }
      const rowByName = new Map<string, number>();
      data.forEach((item, index) => {
        if (item?.Name) rowByName.set(item.Name.trim(), index);
      });
      let updatedData = [...data];
      const touchedRows = new Set<number>();
      let updatedCells = 0;
      let missingNameRows = 0;
      let missingTargetRows = 0;
      let blockedCells = 0;

      parsedItems.forEach((parsedItem) => {
        const itemName = typeof parsedItem.Name === 'string' ? parsedItem.Name.trim() : '';
        if (!itemName) {
          missingNameRows++;
          return;
        }
        const targetIndex = rowByName.get(itemName);
        if (targetIndex === undefined) {
          missingTargetRows++;
          return;
        }
        const baselineRow = updatedData[targetIndex];
        if (!baselineRow) return;
        Object.entries(parsedItem as Record<string, unknown>).forEach(([field, rawValue]) => {
          if (field === 'Name' || field.startsWith('__')) return;
          const nextValue = rawValue == null ? '' : String(rawValue);
          const currentValue = String(
            (updatedData[targetIndex] as Record<string, unknown>)[field] ?? ''
          );
          if (currentValue === nextValue) return;
          if (!canEditCell(baselineRow, field)) {
            blockedCells++;
            return;
          }
          const applied = applyFieldValueToRows(
            updatedData, [targetIndex], field as keyof QuickFillItem, nextValue
          );
          updatedData = applied.updatedData;
          applied.touchedRowIndices.forEach((rowIndex) => touchedRows.add(rowIndex));
          updatedCells++;
        });
      });

      if (touchedRows.size > 0) {
        captureSessionUndoBeforeMutation('Paste');
        setQuickFillData(updatedData);
        snackbar.showSuccess(
          `Pasted ${updatedCells} value${updatedCells === 1 ? '' : 's'} across ${touchedRows.size} row${touchedRows.size === 1 ? '' : 's'}`
        );
      } else {
        snackbar.showWarning('No matching editable cells were updated');
      }
      const warnings: string[] = [];
      if (missingNameRows > 0) {
        warnings.push(`${missingNameRows} row${missingNameRows === 1 ? '' : 's'} skipped: missing Name`);
      }
      if (missingTargetRows > 0) {
        warnings.push(`${missingTargetRows} row${missingTargetRows === 1 ? '' : 's'} skipped: Name not found in table`);
      }
      if (blockedCells > 0) {
        warnings.push(`${blockedCells} cell${blockedCells === 1 ? '' : 's'} skipped: read-only or unavailable`);
      }
      if (warnings.length > 0) snackbar.showWarning(warnings.join('; '));
    } catch {
      snackbar.showError('Failed to paste from clipboard');
    }
  }, [
    applyFieldValueToRows, canEditCell, captureSessionUndoBeforeMutation,
    quickFillData, setQuickFillData, snackbar,
  ]);

  const handleClearSelectedCells = useCallback((target?: QuickFillClearTarget) => {
    const data = Array.isArray(quickFillData) ? quickFillData : [];
    const targetRows = target
      ? Array.from(new Set(target.rowIndices)).filter(
        (index) => Number.isInteger(index) && index >= 0 && index < data.length
      ).sort((left, right) => left - right)
      : selectedRows;
    const columnLabels = target
      ? Array.from(new Set(target.columnLabels.map((label) => label.trim())))
        .filter((label) => label !== '' && label !== 'Name')
      : (properties || []).filter(
        (property) => property?.checked && property.label !== 'Name'
      ).map((property) => property.label);
    if (targetRows.length === 0 || columnLabels.length === 0) return;
    let updatedData = [...data];
    const touchedRows = new Set<number>();
    let clearedCells = 0;
    columnLabels.forEach((label) => {
      const applied = applyFieldValueToRows(
        updatedData, targetRows, label as keyof QuickFillItem, ''
      );
      updatedData = applied.updatedData;
      applied.touchedRowIndices.forEach((index) => touchedRows.add(index));
      clearedCells += applied.touchedRowIndices.length;
    });
    if (clearedCells === 0) {
      snackbar.showWarning('No selected editable cells were cleared');
      return;
    }
    captureSessionUndoBeforeMutation('Clear cells');
    setQuickFillData(updatedData);
    if (target?.scope === 'column' && columnLabels.length === 1) {
      snackbar.showSuccess(`Cleared ${columnLabels[0]} in ${touchedRows.size} rows`);
    } else if (target) {
      snackbar.showSuccess(`Cleared ${clearedCells} selected cell${clearedCells === 1 ? '' : 's'}`);
    } else {
      snackbar.showSuccess(`Cleared ${touchedRows.size} row${touchedRows.size === 1 ? '' : 's'}`);
    }
  }, [
    applyFieldValueToRows, captureSessionUndoBeforeMutation, properties,
    quickFillData, selectedRows, setQuickFillData, snackbar,
  ]);

  const handleFillDown = useCallback(async () => {
    if (selectedRows.length < 2) {
      snackbar.showWarning('Select at least two rows to use Fill down.');
      return;
    }
    const data = Array.isArray(quickFillData) ? quickFillData : [];
    const checkedProps = (properties || []).filter((property) => property?.checked);
    const sortedRows = [...selectedRows].sort((left, right) => left - right);
    const sourceRow = data[sortedRows[0]];
    if (!sourceRow) return;
    let updatedData = [...data];
    const changedRows = new Set<number>();
    sortedRows.slice(1).forEach((targetIndex) => checkedProps.forEach((property) => {
      if (property.label === 'Name') return;
      const sourceValue = sourceRow[property.label as keyof QuickFillItem];
      if (sourceValue === undefined || sourceValue === '') return;
      const applied = applyFieldValueToRows(
        updatedData, [targetIndex], property.label as keyof QuickFillItem, String(sourceValue)
      );
      updatedData = applied.updatedData;
      applied.touchedRowIndices.forEach((index) => changedRows.add(index));
    }));
    if (changedRows.size === 0) {
      snackbar.showWarning('No editable cells were updated by Fill down.');
      return;
    }
    captureSessionUndoBeforeMutation('Fill down');
    setQuickFillData(updatedData);
    // Staged only: the Revit document is written by the Apply/Continue submit path.
    snackbar.showSuccess(`Filled down to ${sortedRows.length - 1} rows from row ${sortedRows[0] + 1}`);
  }, [
    applyFieldValueToRows, captureSessionUndoBeforeMutation,
    properties, quickFillData, selectedRows, setQuickFillData, snackbar,
  ]);

  const handleFillRight = useCallback(async () => {
    if (selectedRows.length === 0) {
      snackbar.showWarning('Select at least one row to use Fill right.');
      return;
    }
    const data = Array.isArray(quickFillData) ? quickFillData : [];
    const checkedProps = (properties || []).filter(
      (property) => property?.checked && property.label !== 'Name'
    );
    if (checkedProps.length < 2) {
      snackbar.showWarning('Enable at least two columns to use Fill right.');
      return;
    }
    const [sourceProp, ...targetProps] = checkedProps;
    const changedRows = new Set<number>();
    let changedCells = 0;
    let updatedData = [...data];
    selectedRows.forEach((rowIndex) => {
      const sourceValue = data[rowIndex]?.[sourceProp.label as keyof QuickFillItem];
      if (sourceValue === undefined || sourceValue === '') return;
      targetProps.forEach((property) => {
        const applied = applyFieldValueToRows(
          updatedData, [rowIndex], property.label as keyof QuickFillItem, String(sourceValue)
        );
        updatedData = applied.updatedData;
        changedCells += applied.touchedRowIndices.length;
        applied.touchedRowIndices.forEach((index) => changedRows.add(index));
      });
    });
    if (changedRows.size === 0) {
      snackbar.showWarning('No editable cells were updated by Fill right.');
      return;
    }
    captureSessionUndoBeforeMutation('Fill right');
    setQuickFillData(updatedData);
    // Staged only: the Revit document is written by the Apply/Continue submit path.
    snackbar.showSuccess(`Filled right across ${changedCells} cells`);
  }, [
    applyFieldValueToRows, captureSessionUndoBeforeMutation,
    properties, quickFillData, selectedRows, setQuickFillData, snackbar,
  ]);

  const handleCut = useCallback(async () => {
    if (selectedRows.length === 0) return;
    try {
      const data = Array.isArray(quickFillData) ? quickFillData : [];
      const selectedItems = selectedRows.map((index) => data[index])
        .filter((item): item is QuickFillItem => Boolean(item));
      if (selectedItems.length === 0) return;
      const visibleColumns = ['Name', ...(properties || [])
        .filter((property) => property?.checked && property.label !== 'Name')
        .map((property) => property.label)];
      await copyToClipboard(serializeToTSV(selectedItems, visibleColumns));
      handleClearSelectedCells();
      snackbar.showSuccess(`Cut ${selectedItems.length} rows to clipboard`);
    } catch {
      snackbar.showError('Failed to cut to clipboard');
    }
  }, [handleClearSelectedCells, properties, quickFillData, selectedRows, snackbar]);

  return {
    handleCopySelected,
    handlePaste,
    handleClearSelectedCells,
    handleFillDown,
    handleFillRight,
    handleCut,
  };
}
